import { atom } from "jotai";
import {
  amortizationExportAtom,
  buildAmortizationWorksheet,
} from "./amortization";
import {
  buildHistoricalsWorksheet,
  historicalsExportAtom,
} from "./historicals";
import { buildOverviewWorksheet, overviewExportAtom } from "./overview";
import {
  buildProjectionsWorksheet,
  projectionsExportAtom,
} from "./projections";
import { buildROIWorksheet, roiExportAtom } from "./roi";
import {
  buildWorkingCapitalWorksheet,
  workingCapitalExportAtom,
} from "./workingCapital";

const XLSXStub = () => import("xlsx-js-style");

type ExportSheetType =
  | "amortization"
  | "projections"
  | "historicals"
  | "overview"
  | "roi"
  | "workingcapital";

const normalizeSheetKey = (value: string): ExportSheetType | null => {
  const normalized = value.trim().toLowerCase();
  if (
    normalized === "amortization" ||
    normalized === "projections" ||
    normalized === "historicals" ||
    normalized === "overview" ||
    normalized === "roi" ||
    normalized === "workingcapital"
  ) {
    return normalized;
  }

  return null;
};

export const exportAtom = atom(
  null,
  async (
    get,
    _set,
    sheetTypes: string[] = [
      "amortization",
      "overview",
      "historicals",
      "projections",
      "roi",
      "workingcapital",
    ],
  ) => {
    const XLSX = await XLSXStub();

    const selected = Array.from(
      new Set(sheetTypes.map(normalizeSheetKey).filter(Boolean)),
    ) as ExportSheetType[];

    if (!selected.length) return;

    const wb = XLSX.utils.book_new();

    selected.forEach((sheetType) => {
      if (sheetType === "overview") {
        const ws = buildOverviewWorksheet(get, XLSX);
        XLSX.utils.book_append_sheet(wb, ws, "Overview");
      }

      if (sheetType === "amortization") {
        const ws = buildAmortizationWorksheet(get, XLSX);
        XLSX.utils.book_append_sheet(wb, ws, "Amortization");
      }

      if (sheetType === "projections") {
        const ws = buildProjectionsWorksheet(get, XLSX);
        XLSX.utils.book_append_sheet(wb, ws, "Projections");
      }

      if (sheetType === "historicals") {
        const ws = buildHistoricalsWorksheet(get, XLSX);
        XLSX.utils.book_append_sheet(wb, ws, "Historicals");
      }

      if (sheetType === "roi") {
        const ws = buildROIWorksheet(get, XLSX);
        XLSX.utils.book_append_sheet(wb, ws, "ROI Metrics");
      }

      if (sheetType === "workingcapital") {
        const ws = buildWorkingCapitalWorksheet(get, XLSX);
        XLSX.utils.book_append_sheet(wb, ws, "Working Capital");
      }
    });

    const fmt = "xlsx";
    const filename = `eta-iq-export-${new Date()
      .toISOString()
      .replace(/:/g, "-")
      .replace(/\..+/, "")}.${fmt}`;

    XLSX.writeFile(wb, filename);
  },
);

export {
  amortizationExportAtom,
  historicalsExportAtom,
  overviewExportAtom,
  projectionsExportAtom,
  roiExportAtom,
  workingCapitalExportAtom,
};
